import React, { useState, useEffect } from 'react';
import { adminService } from '../services/api';
import './EcosystemPanels.css';

const GA4_SETUP_STEPS = [
  'Crear propiedad GA4 en analytics.google.com (gratis)',
  'Habilitar Google Analytics Data API en el proyecto Javiland Panel',
  'Acceso a los datos → añadir https://www.googleapis.com/auth/analytics.readonly',
  'OAuth Playground: reautorizar con webmasters.readonly + analytics.readonly → nuevo refresh token',
  'api/.env → GA4_PROPERTY_ID=123456789 (ID numérico de la propiedad)',
];

const MarketingHub = () => {
  const [gsc, setGsc] = useState(null);
  const [ga4, setGa4] = useState(null);
  const [loadingGsc, setLoadingGsc] = useState(true);
  const [loadingGa4, setLoadingGa4] = useState(true);
  const [gscError, setGscError] = useState(null);
  const [ga4Error, setGa4Error] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingGsc(true);
      setGscError(null);
      try {
        const data = await adminService.getSearchConsoleSummary();
        if (!cancelled) setGsc(data);
      } catch (e) {
        if (!cancelled) {
          setGscError(e.response?.data?.message || 'No se pudo cargar Search Console');
          setGsc(null);
        }
      } finally {
        if (!cancelled) setLoadingGsc(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingGa4(true);
      setGa4Error(null);
      try {
        const data = await adminService.getGa4Summary();
        if (!cancelled) setGa4(data);
      } catch (e) {
        if (!cancelled) {
          setGa4Error(e.response?.data?.message || 'No se pudo cargar GA4');
          setGa4(null);
        }
      } finally {
        if (!cancelled) setLoadingGa4(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="eco-panel">
      <header className="eco-panel-header">
        <h2>Marketing y tráfico</h2>
        <p>Search Console y GA4 en vivo · Plan alineado con Notion (PROC-MKT-002).</p>
      </header>

      <section className="eco-marketing-grid">
        <div className="eco-marketing-card">
          <h3>
            <i className="fab fa-google" aria-hidden /> Google Search Console
          </h3>
          {loadingGsc ? <p>Cargando métricas…</p> : null}
          {gscError ? (
            <div className="eco-gsc-setup">
              <p className="eco-gsc-warn">{gscError}</p>
              <p>
                Configura en <code>api/.env</code>: <code>GSC_CLIENT_ID</code>,{' '}
                <code>GSC_CLIENT_SECRET</code>, <code>GSC_REFRESH_TOKEN</code>,{' '}
                <code>GSC_SITE_URL=sc-domain:javiland.com</code>
              </p>
              <a
                href="https://search.google.com/search-console"
                target="_blank"
                rel="noopener noreferrer"
                className="eco-ext-link"
              >
                Abrir Search Console
              </a>
            </div>
          ) : null}
          {gsc?.configured && gsc.summary ? (
            <ul className="eco-gsc-metrics">
              <li>
                <span>Clics (28 días)</span>
                <strong>{gsc.summary.clicks ?? '—'}</strong>
              </li>
              <li>
                <span>Impresiones (28 días)</span>
                <strong>{gsc.summary.impressions ?? '—'}</strong>
              </li>
              <li>
                <span>CTR</span>
                <strong>
                  {gsc.summary.ctr != null ? `${(gsc.summary.ctr * 100).toFixed(1)}%` : '—'}
                </strong>
              </li>
              <li>
                <span>Posición media</span>
                <strong>
                  {gsc.summary.position != null ? gsc.summary.position.toFixed(1) : '—'}
                </strong>
              </li>
            </ul>
          ) : null}
          {!loadingGsc && !gscError && gsc && !gsc.configured ? (
            <p className="eco-gsc-warn">
              {gsc.message || 'Search Console no configurado en servidor.'}
              {gsc.oauth_error ? ` (${gsc.oauth_error})` : ''}
            </p>
          ) : null}
        </div>

        <div className="eco-marketing-card">
          <h3>
            <i className="fas fa-chart-line" aria-hidden /> Google Analytics 4
          </h3>
          {loadingGa4 ? <p>Cargando métricas…</p> : null}
          {ga4Error ? <p className="eco-gsc-warn">{ga4Error}</p> : null}
          {ga4?.configured && ga4.summary ? (
            <ul className="eco-gsc-metrics">
              <li>
                <span>Sesiones (28 días)</span>
                <strong>{ga4.summary.sessions ?? '—'}</strong>
              </li>
              <li>
                <span>Usuarios activos</span>
                <strong>{ga4.summary.activeUsers ?? '—'}</strong>
              </li>
              <li>
                <span>Vistas de página</span>
                <strong>{ga4.summary.pageViews ?? '—'}</strong>
              </li>
            </ul>
          ) : null}
          {!loadingGa4 && ga4 && !ga4.configured ? (
            <div className="eco-gsc-setup">
              <p className="eco-gsc-warn">
                {ga4.message || 'GA4 no configurado en servidor.'}
                {ga4.oauth_error ? ` (${ga4.oauth_error})` : ''}
              </p>
              <ol className="eco-setup-steps">
                {GA4_SETUP_STEPS.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <p className="eco-card-notes">
                Medición en el sitio público: <code>REACT_APP_GA_TRACKING_ID=G-XXXXXXXX</code> antes de{' '}
                <code>npm run build</code> (archivo <code>frontend/.env.production</code>).
              </p>
              <a
                href="https://analytics.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="eco-ext-link"
              >
                Abrir Google Analytics
              </a>
            </div>
          ) : null}
        </div>

        <div className="eco-marketing-card eco-marketing-card--muted">
          <h3>
            <i className="fas fa-tasks" aria-hidden /> Plan marketing
          </h3>
          <ul className="eco-plan-list">
            <li>Notion: backlog diseño (65 propuestas) — PROC-MKT-002</li>
            <li>Auditoría diseño 2026 en Notion</li>
            <li>
              SEO sitelinks: Tienda · Servicios · Growth Partner — meta + sitemap desplegados
            </li>
          </ul>
          <p className="eco-card-notes">Docs: PROCESO_OPORTUNIDADES_MEJORA_DISENO.md · BACKLOG_MEJORAS_DISENO.md</p>
        </div>
      </section>
    </div>
  );
};

export default MarketingHub;
